import React, { useCallback } from "react";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { SimpleNodeData } from "src/constants/NodeDataTypes";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import {
  NodeEditorBaseProps,
  NodeEditorCompletionContext,
} from "src/nodeEditor/NodeEditor";

type Props = {
  nodeId?: string;
  src?: string;
  language: "sql" | "python" | undefined;
  placeholder?: string;
  onChange: (value: string | undefined) => void;
} & Omit<NodeEditorBaseProps<SimpleNodeData>, "onUpdate">;

export const CodeNodeEditor: React.FC<Props> = ({
  nodeId = "",
  src,
  language,
  onChange,
  immutable,
  isReactive,
  displayedError,
  placeholder,
}) => {
  const isSearchHighlighted = useIsFieldHighlighted(`${nodeId}:src`);
  useFormApplyNodeSuggestion<{ src: string }>(nodeId, (suggestion) => {
    onChange(suggestion.src);
  });

  const onCodeChange = useCallback(
    (codeText: string | undefined) => {
      if (!isReactive && src !== codeText) {
        onChange(codeText ?? "");
      }
    },
    [isReactive, src, onChange],
  );

  const errorHighlightedLine =
    displayedError?.loc_type === "line" &&
    typeof displayedError?.loc === "number"
      ? displayedError?.loc
      : undefined;
  return (
    <NodeEditorCompletionContext.Consumer>
      {({ defaultCompletion }) => {
        return (
          <CodeEditor
            completionExtension={defaultCompletion}
            dataLoc="code-editor"
            disabled={immutable}
            errorHighlightedLine={errorHighlightedLine}
            isSearchHighlighted={isSearchHighlighted}
            language={language}
            placeholder={placeholder}
            value={src}
            onChange={onCodeChange}
          />
        );
      }}
    </NodeEditorCompletionContext.Consumer>
  );
};
